import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface Project {
  project: string;
  id?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-project-selector',
  templateUrl: './project-selector.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProjectSelectorComponent),
      multi: true,
    },
  ],
})
export class ProjectSelectorComponent
  implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() projects: Project[] = [];
  @Input() placeholder: string = 'Select a project...';
  @Input() searchPlaceholder: string = 'Search projects...';
  @Input() disabled: boolean = false;
  @Input() maxHeight: string = '300px';
  @Input() width: string = '300px';

  @Output() selectionChange = new EventEmitter<Project | null>();
  @Output() dropdownOpenChange = new EventEmitter<boolean>();

  @ViewChild('searchInput', { static: false })
  searchInput!: ElementRef<HTMLInputElement>;

  selectedProject: Project | null = null;
  dropdownOpen = false;
  searchTerm = '';
  filteredProjects: Project[] = [];
  activeTab: 'all' | 'favorites' = 'all';
  favoriteProjects: Set<string> = new Set();

  onChange = (project: Project | null) => { };
  onTouched = () => { };

  private clickListener: any;

  ngOnInit() {
    this.loadFavorites();
    this.filteredProjects = [...this.projects];
    this.setupClickListener();
  }

  ngOnDestroy() {
    this.removeClickListener();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (!this.dropdownOpen) return;

    switch (event.key) {
      case 'Escape':
        this.closeDropdown();
        break;
      case 'Enter':
        if (this.filteredProjects.length > 0) {
          this.selectProject(this.filteredProjects[0]);
        }
        break;
    }
  }

  private setupClickListener() {
    this.clickListener = (event: MouseEvent) => {
      const element = event.target as HTMLElement;
      if (!element.closest('.relative')) {
        this.closeDropdown();
      }
    };
    document.addEventListener('click', this.clickListener);
  }

  private removeClickListener() {
    if (this.clickListener) {
      document.removeEventListener('click', this.clickListener);
    }
  }

  toggleDropdown(): void {
    if (this.disabled) return;

    this.dropdownOpen = !this.dropdownOpen;
    this.dropdownOpenChange.emit(this.dropdownOpen);

    if (this.dropdownOpen) {
      this.searchTerm = '';
      this.filterProjects();
      setTimeout(() => {
        this.searchInput?.nativeElement?.focus();
      }, 100);
    }
  }

  closeDropdown(): void {
    this.dropdownOpen = false;
    this.searchTerm = '';
    this.filterProjects();
    this.dropdownOpenChange.emit(false);
  }

  onSearchChange(): void {
    this.filterProjects();
  }

  private filterProjects(): void {
    const projectsToFilter =
      this.activeTab === 'favorites'
        ? this.getFavoriteProjects()
        : this.projects;

    if (!this.searchTerm.trim()) {
      this.filteredProjects = projectsToFilter.sort((a, b) =>
        a.project.toLowerCase().localeCompare(b.project.toLowerCase())
      );
    } else {
      this.filteredProjects = projectsToFilter
        .filter((project) =>
          project.project.toLowerCase().includes(this.searchTerm.toLowerCase())
        )
        .sort((a, b) =>
          a.project.toLowerCase().localeCompare(b.project.toLowerCase())
        );
    }
  }

  selectProject(project: Project): void {
    this.selectedProject = project;
    this.onChange(project);
    this.selectionChange.emit(project);
    this.closeDropdown();
  }

  clearSelection(): void {
    this.selectedProject = null;
    this.onChange(null);
    this.selectionChange.emit(null);
  }

  getDisplayText(): string {
    return this.selectedProject?.project || this.placeholder;
  }

  isProjectSelected(project: Project): boolean {
    return this.selectedProject?.project === project.project;
  }

  // ControlValueAccessor implementation
  writeValue(project: Project | null): void {
    this.selectedProject = project;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  trackByProject(index: number, project: Project): string {
    return project.project;
  }

  // Favorites management
  loadFavorites(): void {
    try {
      const favorites = localStorage.getItem('project-favorites');
      if (favorites) {
        this.favoriteProjects = new Set(JSON.parse(favorites));
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  }

  saveFavorites(): void {
    try {
      localStorage.setItem(
        'project-favorites',
        JSON.stringify([...this.favoriteProjects])
      );
    } catch (error) {
      console.error('Error saving favorites:', error);
    }
  }

  toggleFavorite(project: Project, event: Event): void {
    event.stopPropagation();
    const projectId = project.id || project.project;

    if (this.favoriteProjects.has(projectId)) {
      this.favoriteProjects.delete(projectId);
    } else {
      this.favoriteProjects.add(projectId);
    }

    this.saveFavorites();
    this.filterProjects();
  }

  isFavorite(project: Project): boolean {
    const projectId = project.id || project.project;
    return this.favoriteProjects.has(projectId);
  }

  getFavoriteProjects(): Project[] {
    return this.projects.filter((project) => this.isFavorite(project));
  }

  getAllProjectsCount(): number {
    return this.projects.length;
  }

  getFavoritesCount(): number {
    return this.getFavoriteProjects().length;
  }

  switchTab(tab: 'all' | 'favorites'): void {
    this.activeTab = tab;
    this.filterProjects();
  }
}
